import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './AutoJobApply.css';
import ProductSidebar from './ProductSidebar';
import ConnectAccounts from './ConnectAccounts';

const STEPS = ['Profile Setup', 'Job Preferences', 'Resume Upload', 'Review & Launch'];

const AutoJobApply = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ applied: 0, skipped: 0, failed: 0 });
  const [wsConnected, setWsConnected] = useState(false);
  const [showLinkedInWarning, setShowLinkedInWarning] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [resumePath, setResumePath] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [canStart, setCanStart] = useState(false);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const consoleEndRef = useRef(null);

  const getApiBase = () => {
  const env = process.env.REACT_APP_API_BASE_URL;
  if (env) return env;
    const origin = window.location.origin;
    if (origin.startsWith('http://') || origin.startsWith('https://')) return origin;
    return 'http://localhost:5000'; // fallback for Electron
  };
  const API_BASE_URL = getApiBase();
  const WS_URL = API_BASE_URL.replace(/^http/, 'ws');
  
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [profileResume, setProfileResume] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  const [showReviewSection, setShowReviewSection] = useState(false);
  

  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    portfolio: '',
    yearsExperience: '',
    currentTitle: '',
    noticePeriod: 'Immediate',
    expectedSalary: '',
  });

  const [preferences, setPreferences] = useState({
    jobTitles: '',
    locations: '',
    jobType: 'Full-time',
    remote: 'Any',
    minSalary: '',
    excludeCompanies: '',
    keywords: '',
    //platforms: { linkedin: true, indeed: false, dice: false, monster: false },
    platforms: { linkedin: true, dice: false, monster: false },
    maxApplications: '20',
    autoTailor: true,
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRunning) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRunning]);

  const [showNavBlockModal, setShowNavBlockModal] = useState(false);
  const [pendingNavUrl, setPendingNavUrl] = useState(null);

  useEffect(() => {
    if (!isRunning) return;
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto')) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavUrl(href);
      setShowNavBlockModal(true);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isRunning]);

  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auto-apply/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.isRunning) { setIsRunning(true); setCurrentStep(3); connectWebSocket(); }
      } catch {}
    };
    if (user) checkStatus();
  }, [user]);

  useEffect(() => {
    setCanStart(false);
  }, [preferences.platforms]);

  useEffect(() => {
    const fetchProfileResume = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {

          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (data.profile) {
          const filename = data.profile.resumeFilename || null;
          setProfileResume(filename);
          if (filename) {
            setResumeChoice('profile');
            setResumePath(`uploads/resumes/${filename}`);
            setResumeName(filename);
          }
          setProfile(prev => ({
            ...prev,
            fullName:     data.profile.fullName  || prev.fullName,
            email:        data.profile.email     || prev.email,
            phone:        data.profile.phone     || prev.phone,
            location:     data.profile.location  || prev.location,
            linkedIn:     data.profile.linkedIn  || prev.linkedIn,
            portfolio:    data.profile.portfolio || prev.portfolio,
            currentTitle: data.profile.jobTitle  || prev.currentTitle,
          }));
        }
      } catch {}
    };
    if (user) fetchProfileResume();
  }, [user]);

  const addClientLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/ws/auto-apply?token=${token}`);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setLogs(prev => [...prev, { message: data.message, type: data.level || 'info', time: data.time }]);
        } else if (data.type === 'job_result') {
          setAppliedJobs(prev => [...prev, data.job]);
          setStats(prev => ({ ...prev, [data.job.status]: (prev[data.job.status] || 0) + 1 }));
        } else if (data.type === 'complete') {
          setIsRunning(false);
          if (data.stats) setStats(data.stats);
          setLogs(prev => [...prev, { message: '🎯 Session completed!', type: 'success', time: new Date().toLocaleTimeString() }]);
        } else if (data.type === 'error') {
          setLogs(prev => [...prev, { message: `❌ ${data.message}`, type: 'error', time: new Date().toLocaleTimeString() }]);
          setIsRunning(false);
        }
      } catch {}
    };
    ws.onclose = () => { setWsConnected(false); wsRef.current = null; };
    ws.onerror = () => setWsConnected(false);
  }, [WS_URL]);

  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/upload-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) { setResumePath(data.resumePath); setResumeName(file.name); }
      else addClientLog(`❌ Upload failed: ${data.message}`, 'error');
    } catch (err) {
      addClientLog(`❌ Upload error: ${err.message}`, 'error');
    } finally {
      setUploadingResume(false);
    }
  };

  const handleLaunch = async () => {
    if (!preferences.jobTitles.trim()) { addClientLog('⚠️ Enter at least one job title', 'warning'); setCurrentStep(1); return; }
    if (!preferences.locations.trim()) { addClientLog('⚠️ Enter at least one location', 'warning'); setCurrentStep(1); return; }
    if (!Object.values(preferences.platforms).some(Boolean)) { addClientLog('⚠️ Select at least one platform', 'warning'); setCurrentStep(1); return; }
    setIsRunning(true); setLogs([]); setAppliedJobs([]); setStats({ applied: 0, skipped: 0, failed: 0 });
    connectWebSocket();
    await new Promise(r => setTimeout(r, 800));
    addClientLog('🚀 Launching Auto Apply Agent...', 'info');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-apply/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          profile,
          preferences,
          resumePath: resumeChoice === 'profile' ? null : resumePath,
          useProfileResume: resumeChoice === 'profile'
        })
      });
      const data = await res.json();
      if (res.ok) { setSessionId(data.sessionId); addClientLog(`✅ Session started (ID: ${data.sessionId})`, 'success'); }
      else { addClientLog(`❌ ${data.message}`, 'error'); setIsRunning(false); }
    } catch (err) {
      addClientLog(`❌ Server error: ${err.message}`, 'error');
      addClientLog('💡 Make sure your backend server is running with Puppeteer installed', 'warning');
      setIsRunning(false);
    }
  };

  const handleStop = async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ action: 'stop' }));
    try { await fetch(`${API_BASE_URL}/api/auto-apply/stop`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); } catch {}
    setIsRunning(false);
    addClientLog('⏹️ Agent stopped', 'warning');
  };

  const [fieldErrors, setFieldErrors] = useState({});

  const validateStep = (step) => {
    const errors = {};
    if (step === 0) {
      if (!profile.fullName.trim()) errors.fullName = true;
      if (!profile.email.trim()) errors.email = true;
      if (!profile.phone.trim()) errors.phone = true;
      if (!profile.location.trim()) errors.location = true;
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        addClientLog('⚠️ Please fill in all required fields', 'warning');
        return false;
      }
    }
    if (step === 1) {
      if (!preferences.jobTitles.trim()) errors.jobTitles = true;
      if (!preferences.locations.trim()) errors.locations = true;
      if (!Object.values(preferences.platforms).some(Boolean)) errors.platforms = true;
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        addClientLog('⚠️ Please fill in all required fields', 'warning');
        return false;
      }
    }
    if (step === 2) {
      if (!resumePath && !profileResume) { addClientLog('⚠️ Please upload a resume', 'warning'); return false; }
    }
    setFieldErrors({});
    return true;
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const prevStep = () => setCurrentStep(s => Math.max(s - 1, 0));

  const renderStepIndicator = () => (
    <div className="ajp-steps">
      {STEPS.map((step, i) => (
        <div key={i} className={`ajp-step ${i === currentStep ? 'active' : i < currentStep ? 'done' : ''}`}
          onClick={() => !isRunning && setCurrentStep(i)} style={{ cursor: isRunning ? 'default' : 'pointer' }}>
          <div className="ajp-step-circle">{i < currentStep ? '✓' : i + 1}</div>
          <span className="ajp-step-label">{step}</span>
          {i < STEPS.length - 1 && <div className="ajp-step-line" />}
        </div>
      ))}
    </div>
  );

  const renderStep0 = () => (
    <div className="ajp-form-grid">
      <h3 className="ajp-section-title">👤 Personal Information</h3>
      <div className="ajp-row">
        <div className="ajp-field"><label>Full Name <span className="ajp-required">*</span></label><input className={fieldErrors.fullName ? 'ajp-field-error' : ''} value={profile.fullName} onChange={e => { setProfile({ ...profile, fullName: e.target.value }); setFieldErrors(f => ({ ...f, fullName: false })); }} placeholder="John Doe" /></div>
        <div className="ajp-field"><label>Email Address <span className="ajp-required">*</span></label><input className={fieldErrors.email ? 'ajp-field-error' : ''} type="email" value={profile.email} onChange={e => { setProfile({ ...profile, email: e.target.value }); setFieldErrors(f => ({ ...f, email: false })); }} placeholder="john@email.com" /></div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Phone Number <span className="ajp-required">*</span></label><input className={fieldErrors.phone ? 'ajp-field-error' : ''} value={profile.phone} onChange={e => { setProfile({ ...profile, phone: e.target.value }); setFieldErrors(f => ({ ...f, phone: false })); }} placeholder="+1 (555) 000-0000" /></div>
        <div className="ajp-field"><label>Current Location <span className="ajp-required">*</span></label><input className={fieldErrors.location ? 'ajp-field-error' : ''} value={profile.location} onChange={e => { setProfile({ ...profile, location: e.target.value }); setFieldErrors(f => ({ ...f, location: false })); }} placeholder="New York, USA" /></div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>LinkedIn URL</label><input value={profile.linkedIn} onChange={e => setProfile({ ...profile, linkedIn: e.target.value })} placeholder="linkedin.com/in/johndoe" /></div>
        <div className="ajp-field"><label>Portfolio / GitHub</label><input value={profile.portfolio} onChange={e => setProfile({ ...profile, portfolio: e.target.value })} placeholder="github.com/johndoe" /></div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Current Job Title</label><input value={profile.currentTitle} onChange={e => setProfile({ ...profile, currentTitle: e.target.value })} placeholder="Frontend Developer" /></div>
        <div className="ajp-field"><label>Years of Experience</label>
          <select value={profile.yearsExperience} onChange={e => setProfile({ ...profile, yearsExperience: e.target.value })}>
            <option value="">Select</option><option>0-1 years</option><option>1-3 years</option><option>3-5 years</option><option>5-8 years</option><option>8+ years</option>
          </select>
        </div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Notice Period</label>
          <select value={profile.noticePeriod} onChange={e => setProfile({ ...profile, noticePeriod: e.target.value })}>
            <option>Immediate</option><option>2 weeks</option><option>1 month</option><option>2 months</option><option>3 months</option>
          </select>
        </div>
        <div className="ajp-field"><label>Expected Salary (USD/year)</label><input value={profile.expectedSalary} onChange={e => setProfile({ ...profile, expectedSalary: e.target.value })} placeholder="$90,000" /></div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="ajp-form-grid">
      <h3 className="ajp-section-title">🎯 Job Preferences</h3>
      <div className="ajp-row">
        <div className="ajp-field ajp-field-full"><label>Job Titles <span className="ajp-required">*</span></label>
          <input className={fieldErrors.jobTitles ? 'ajp-field-error' : ''} value={preferences.jobTitles} onChange={e => { setPreferences({ ...preferences, jobTitles: e.target.value }); setFieldErrors(f => ({ ...f, jobTitles: false })); }} placeholder="Frontend Developer, React Engineer, UI Developer" />
          <span className="ajp-hint">Separate with commas</span>
        </div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Locations <span className="ajp-required">*</span></label>
          <input className={fieldErrors.locations ? 'ajp-field-error' : ''} value={preferences.locations} onChange={e => { setPreferences({ ...preferences, locations: e.target.value }); setFieldErrors(f => ({ ...f, locations: false })); }} placeholder="New York, Remote" />
        </div>
        <div className="ajp-field"><label>Job Type</label>
          <select value={preferences.jobType} onChange={e => setPreferences({ ...preferences, jobType: e.target.value })}>
            <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
          </select>
        </div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Remote Preference</label>
          <select value={preferences.remote} onChange={e => setPreferences({ ...preferences, remote: e.target.value })}>
            <option>Any</option><option>Remote Only</option><option>Hybrid</option><option>On-site Only</option>
          </select>
        </div>
        <div className="ajp-field"><label>Max Applications</label>
          <select value={preferences.maxApplications} onChange={e => setPreferences({ ...preferences, maxApplications: e.target.value })}>
            <option>10</option><option>20</option><option>50</option><option>100</option>
          </select>
        </div>
      </div>
      <div className="ajp-row">
        <div className="ajp-field"><label>Exclude Companies</label><input value={preferences.excludeCompanies} onChange={e => setPreferences({ ...preferences, excludeCompanies: e.target.value })} placeholder="Company A, Company B" /></div>
        <div className="ajp-field"><label>Keywords</label><input value={preferences.keywords} onChange={e => setPreferences({ ...preferences, keywords: e.target.value })} placeholder="React, TypeScript, Node.js" /></div>
      </div>

      <h3 className="ajp-section-title">🌐 Job Platforms</h3>
      <div className="ajp-platforms">
        {[{ key: 'linkedin', label: 'LinkedIn', icon: '🔷', note: 'Easy Apply' },
          //{ key: 'indeed', label: 'Indeed', icon: '🔵', note: 'Indeed Apply' },
          { key: 'dice', label: 'Dice', icon: '🎲', note: 'Tech Jobs' },
          { key: 'monster', label: 'Monster', icon: '👾', note: 'All Jobs' }
        ].map(({ key, label, icon, note }) => (
          <label key={key} className={`ajp-platform-card ${preferences.platforms[key] ? 'enabled' : ''}`}>
            <input type="checkbox" checked={preferences.platforms[key]}
              onChange={() => setPreferences({ ...preferences, platforms: { ...preferences.platforms, [key]: !preferences.platforms[key] } })} />
            <span className="platform-icon">{icon}</span>
            <span className="platform-name">{label}</span>
            <span className="platform-note">{note}</span>
            {preferences.platforms[key] && <span className="platform-badge">Active</span>}
          </label>
        ))}
      </div>

    </div>
  );

  const renderStep2 = () => (
    <div className="ajp-form-grid">
      <h3 className="ajp-section-title">📄 Upload Your Resume</h3>

      {profileResume ? (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={() => {
                setResumeChoice('profile');
                setResumePath(`uploads/resumes/${profileResume}`);
                setResumeName(profileResume);
              }}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                border: `2px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
              }}
            >
              <div style={{ fontWeight: '600' }}>✅ Use Profile Resume</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>{profileResume}</div>
            </button>
            <button
              type="button"
              onClick={() => {
                setResumeChoice('upload');
                setResumePath('');
                setResumeName('');
              }}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                border: `2px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left'
              }}
            >
              <div style={{ fontWeight: '600' }}>📄 Upload New Resume</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px' }}>Choose a different file</div>
            </button>
          </div>

          {resumeChoice === 'profile' && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
              background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
            }}>
              ✓ Your profile resume will be used for auto job applications.
            </div>
          )}

          {resumeChoice === 'upload' && (
            <div className={`ajp-dropzone ${resumeName ? 'has-file' : ''} ${uploadingResume ? 'uploading' : ''}`}
              onClick={() => !uploadingResume && fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFileUpload(e.dataTransfer.files[0]); }}>
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
                onChange={e => handleFileUpload(e.target.files[0])} />
              {uploadingResume
                ? <><div className="ajp-dropzone-icon">⏳</div><div className="ajp-dropzone-title">Uploading...</div></>
                : resumeName
                  ? <><div className="ajp-dropzone-icon">✅</div><div className="ajp-dropzone-title">{resumeName}</div>
                      <div className="ajp-dropzone-sub">Uploaded · Click to replace</div></>
                  : <><div className="ajp-dropzone-icon">📎</div><div className="ajp-dropzone-title">Drop your resume here</div>
                      <div className="ajp-dropzone-sub">PDF, DOC, DOCX · Click to browse</div></>}
            </div>
          )}
        </>
      ) : (
        <div className={`ajp-dropzone ${resumeName ? 'has-file' : ''} ${uploadingResume ? 'uploading' : ''}`}
          onClick={() => !uploadingResume && fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFileUpload(e.dataTransfer.files[0]); }}>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
            onChange={e => handleFileUpload(e.target.files[0])} />
          {uploadingResume
            ? <><div className="ajp-dropzone-icon">⏳</div><div className="ajp-dropzone-title">Uploading...</div></>
            : resumeName
              ? <><div className="ajp-dropzone-icon">✅</div><div className="ajp-dropzone-title">{resumeName}</div>
                  <div className="ajp-dropzone-sub">Uploaded · Click to replace</div></>
              : <><div className="ajp-dropzone-icon">📎</div><div className="ajp-dropzone-title">Drop your resume here</div>
                  <div className="ajp-dropzone-sub">PDF, DOC, DOCX · Click to browse</div></>}
        </div>
      )}

      <h3 className="ajp-section-title" style={{ marginTop: '24px' }}>🤖 AI Settings</h3>
      <div className="ajp-toggles">
        <label className="ajp-toggle-row">
          <div className="ajp-toggle-info"><span>Auto-tailor Resume</span><small>Adjust resume keywords to match each job description</small></div>
          <div className={`ajp-toggle ${preferences.autoTailor ? 'on' : ''}`} onClick={() => setPreferences({ ...preferences, autoTailor: !preferences.autoTailor })}>
            <div className="ajp-toggle-thumb" />
          </div>
        </label>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="ajp-form-grid">
      <h3 className="ajp-section-title">🚀 Review & Launch</h3>
      <div className="ajp-review-dropdown">
        <button 
          className="ajp-review-toggle" 
          onClick={() => setShowReviewSection(prev => !prev)}
        >
          <span>📋 Review Your Settings</span>
          <span className={`ajp-accordion-chevron ${showReviewSection ? 'open' : ''}`}>▾</span>
        </button>

        {showReviewSection && (
          <div className="ajp-review-grid">
            <div className="ajp-review-card glass"><h4>👤 Profile</h4>
              <p><span>Name:</span> {profile.fullName || '—'}</p><p><span>Email:</span> {profile.email || '—'}</p>
              <p><span>Location:</span> {profile.location || '—'}</p><p><span>Title:</span> {profile.currentTitle || '—'}</p>
              <p><span>Experience:</span> {profile.yearsExperience || '—'}</p>
            </div>
            <div className="ajp-review-card glass"><h4>🎯 Preferences</h4>
              <p><span>Roles:</span> {preferences.jobTitles || '—'}</p><p><span>Locations:</span> {preferences.locations || '—'}</p>
              <p><span>Type:</span> {preferences.jobType}</p><p><span>Remote:</span> {preferences.remote}</p>
              <p><span>Max Apps:</span> {preferences.maxApplications}</p>
            </div>
            <div className="ajp-review-card glass"><h4>🌐 Platforms</h4>
              {Object.entries(preferences.platforms).filter(([, v]) => v).map(([p]) => (<p key={p}><span>✅</span> {p.charAt(0).toUpperCase() + p.slice(1)}</p>))}
              {!Object.values(preferences.platforms).some(Boolean) && <p style={{ color: '#f87171' }}>⚠️ None selected</p>}
            </div>
            <div className="ajp-review-card glass"><h4>🤖 AI Features</h4>
              <p><span>Tailoring:</span> {preferences.autoTailor ? '✅ On' : '❌ Off'}</p>
              <p><span>Resume:</span> {resumeName || <span style={{ color: '#fbbf24' }}>⚠️ Not uploaded</span>}</p>
            </div>
          </div>
        )}
      </div>

      <div className="ajp-ws-status">
        <div className={`ajp-ws-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
        <span>{wsConnected ? '🟢 Agent server connected' : '🔴 Agent server disconnected'}</span>
        {!wsConnected && <button className="ajp-reconnect-btn" onClick={connectWebSocket}>Reconnect</button>}
      </div>

      {/* ── Stat Cards ── */}
      <div className="ajp-stat-cards">
        <div className="ajp-stat-card ajp-stat-total">
          <div className="ajp-stat-card-top">
            <span className="ajp-stat-icon">🎯</span>
            <span className="ajp-stat-trend">↗</span>
          </div>
          <div className="ajp-stat-number">{stats.applied + stats.skipped + stats.failed}</div>
          <div className="ajp-stat-label">Total Applications</div>
        </div>
        <div className="ajp-stat-card ajp-stat-applied">
          <div className="ajp-stat-card-top">
            <span className="ajp-stat-icon">✅</span>
            <span className="ajp-stat-trend">↗</span>
          </div>
          <div className="ajp-stat-number">{stats.applied}</div>
          <div className="ajp-stat-label">Successfully Applied</div>
        </div>
        <div className="ajp-stat-card ajp-stat-failed">
          <div className="ajp-stat-card-top">
            <span className="ajp-stat-icon">❌</span>
            <span className="ajp-stat-trend">↗</span>
          </div>
          <div className="ajp-stat-number">{stats.failed}</div>
          <div className="ajp-stat-label">Failed</div>
        </div>
        <div className="ajp-stat-card ajp-stat-skipped">
          <div className="ajp-stat-card-top">
            <span className="ajp-stat-icon">⏳</span>
            <span className="ajp-stat-trend">↗</span>
          </div>
          <div className="ajp-stat-number">{stats.skipped}</div>
          <div className="ajp-stat-label">In Queue</div>
        </div>
      </div>

      {/* ── Console ── */}
      <div className="ajp-console glass">
        <div className="ajp-console-header">
          <span>🖥️ Live Application Console</span>
        </div>
        <div className="ajp-console-body">
          {logs.length === 0 && !isRunning && <div className="ajp-console-empty">Press "Launch Auto Apply" to start the real agent...</div>}
          {logs.map((log, i) => (
            <div key={i} className={`ajp-log ajp-log-${log.type}`}>
              <span className="ajp-log-time">[{log.time}]</span><span>{log.message}</span>
            </div>
          ))}
          <div ref={consoleEndRef} />
        </div>
      </div>

      {appliedJobs.length > 0 && (
        <div className="ajp-jobs-cards">
          <h4 className="ajp-jobs-cards-title">📋 Applications This Session <span className="ajp-jobs-count">{appliedJobs.length} jobs</span></h4>
          <div className="ajp-cards-grid">
            {appliedJobs.map((job, i) => {
              const status = job.status;
              const statusLabel = status === 'applied' ? '✅ Applied' : status === 'skipped' ? '⏭️ Skipped' : status === 'applying' ? '⏳ Applying...' : '❌ Failed';
              return (
                <div key={i} className={`ajp-job-card glass ajp-job-card--${status}`}>
                  <div className="ajp-job-card-left">
                    <div className="ajp-job-card-title">{job.title || job.job_title}</div>
                    <div className="ajp-job-card-meta">
                      <span>🏢 {job.company || '—'}</span>
                      {job.location && <span>📍 {job.location}</span>}
                      <span>🕐 {job.appliedAt ? new Date(job.appliedAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</span>
                    </div>
                  </div>
                  <div className="ajp-job-card-right">
                    <span className="ajp-platform-tag">{job.platform}</span>
                    <span className={`ajp-job-status-badge ajp-job-status--${status}`}>{statusLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Connect Accounts — shown only when not running */}
      {!isRunning && (
        <ConnectAccounts
          token={localStorage.getItem('token')}
          userId={user?.id}
          platforms={preferences.platforms}
          onReady={() => setCanStart(true)}
          apiBaseUrl={API_BASE_URL}
        />
      )}

      <div className="ajp-launch-actions">
        {!isRunning
          ? (
            <button
              className="ajp-launch-btn"
              onClick={() => preferences.platforms.linkedin ? setShowLinkedInWarning(true) : handleLaunch()}
              disabled={!canStart}
              style={{ opacity: canStart ? 1 : 0.5, cursor: canStart ? 'pointer' : 'not-allowed' }}
            >
              🚀 Launch Auto Apply
            </button>
          )
          : <button className="ajp-stop-btn" onClick={handleStop}>⏹️ Stop Agent</button>}
        {isRunning && (
          <div className="ajp-running-indicator">
            <div className="ajp-pulse" />Agent running... ({stats.applied} applied)
          </div>
        )}
        {sessionId && !isRunning && stats.applied > 0 && (
          <button className="ajp-btn-secondary" onClick={() => navigate('/dashboard')}>
            📊 View in Dashboard
          </button>
        )}
      </div>

      <div className="ajp-notice">
        <strong>⚠️ How it works:</strong> Log in once via the Connect Accounts panel above — sessions are saved automatically. The agent then runs fully headless on the server.
      </div>
    </div>
  );

  const steps = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <div className="neural-glass-homepage">
      <div className="neural-background" />
      <div className="geometric-shapes">
        <div className="shape shape-1" /><div className="shape shape-2" /><div className="shape shape-3" /><div className="shape shape-4" />
      </div>
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      {/* Block navigation while agent is running */}
      {showNavBlockModal && (
        <div className="ajp-nav-block-overlay" onClick={() => setShowNavBlockModal(false)}>
          <div className="ajp-nav-block-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-nav-block-icon">⚠️</div>
            <h3>Agent is still running</h3>
            <p>Leaving this page will not stop the automation. Do you want to stop the agent first?</p>
            <div className="ajp-nav-block-actions">
              <button
                className="ajp-nav-block-stop"
                onClick={async () => {
                  await handleStop();
                  setShowNavBlockModal(false);
                  if (pendingNavUrl) navigate(pendingNavUrl);
                }}
              >
                Stop Agent &amp; Leave
              </button>
              <button className="ajp-nav-block-stay" onClick={() => { setShowNavBlockModal(false); setPendingNavUrl(null); }}>
                Stay on Page
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="ajp-main">
          <div className="li-hero">
            <div className="li-hero-badge">🚀 AI-Powered</div>
            <h1 className="li-hero-title">
              AI Auto <span className="li-gradient-text">Job Apply</span>
            </h1>
            <p className="li-hero-subtitle">
              Real browser automation — applies to jobs on LinkedIn, Indeed, Dice & Monster on your behalf.
            </p>
          </div>
        <div className="ajp-hero">
          <div className="ajp-hero-badges">
            <span className="ajp-badge">⚡ Real Browser Automation</span>
            <span className="ajp-badge">🔷 LinkedIn</span>
            <span className="ajp-badge">🔵 Indeed</span>
            <span className="ajp-badge">🎲 Dice</span>
            <span className="ajp-badge">👾 Monster</span>
          </div>
        </div>
        <div className="ajp-container glass">
          {renderStepIndicator()}
          <div className="ajp-step-content">{steps[currentStep]()}</div>
          <div className="ajp-nav-buttons">
            {currentStep > 0 && !isRunning && <button className="ajp-btn-secondary" onClick={prevStep}>← Back</button>}
            {currentStep < STEPS.length - 1 && <button className="ajp-btn-primary" onClick={nextStep}>Next →</button>}
          </div>
        </div>
      </main>
      {showLinkedInWarning && (
        <div className="ajp-modal-overlay" onClick={() => setShowLinkedInWarning(false)}>
          <div className="ajp-modal glass" onClick={e => e.stopPropagation()}>
            <div className="ajp-modal-icon">⚠️</div>
            <h3 className="ajp-modal-title">LinkedIn Automation Warning</h3>
            <p className="ajp-modal-body">
              LinkedIn's Terms of Service prohibit automated job applications. While Terview automates <strong>your own account</strong> on your behalf, LinkedIn may:
            </p>
            <ul className="ajp-modal-list">
              <li>Temporarily restrict or permanently ban your account</li>
              <li>Flag your profile for suspicious activity</li>
            </ul>
            <p className="ajp-modal-body">
              By continuing, you acknowledge that <strong>you use LinkedIn automation at your own risk</strong>. Terview is not liable for any account restrictions.
            </p>
            <div className="ajp-modal-actions">
              <button className="ajp-modal-cancel" onClick={() => setShowLinkedInWarning(false)}>Cancel</button>
              <button className="ajp-modal-confirm" onClick={() => { setShowLinkedInWarning(false); handleLaunch(); }}>I Understand, Continue</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AutoJobApply;